import React, { useState } from 'react';
import {
  Box, Card, CardContent, Button, FormControlLabel, Switch, Typography, Chip, Stack,
  Accordion, AccordionSummary, AccordionDetails, Alert, CircularProgress, Divider
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import PageHeader from '../components/PageHeader';
import JsonViewer from '../components/JsonViewer';
import api from '../api';
import { useAuth } from '../context/AuthContext';

const Entitlements = () => {
  const { entitlements: initial } = useAuth();
  const [data, setData] = useState(initial || null);
  const [fresh, setFresh] = useState(false);
  const [cached, setCached] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchNow = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/entitlements', { params: fresh ? { fresh: 'true' } : {} });
      setData(res.data.data);
      setCached(res.data.cached);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const roles = data?.roles || [];
  const canonical = data?.entitlements?.canonical_permissions || [];
  const product = data?.entitlements?.product_permissions || {};

  return (
    <Box>
      <PageHeader
        title="Entitlements"
        subtitle="Roles and permissions resolved by the gateway. The product enforces locally. Product permissions is cached for 30s on the gateway."
        action={
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControlLabel
              control={<Switch checked={fresh} onChange={(e) => setFresh(e.target.checked)} />}
              label="fresh (bypass cache)"
            />
            <Button variant="contained" startIcon={<RefreshIcon />} onClick={fetchNow} disabled={loading}>
              Fetch
            </Button>
          </Stack>
        }
      />

      {cached !== null && !loading && (
        <Alert severity={cached ? 'info' : 'success'} sx={{ mb: 2 }}>
          Served {cached ? 'from cache' : 'fresh from the entitlement service'}.
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
      ) : data ? (
        <Stack spacing={3}>
          <Card>
            <CardContent>
              <Typography variant="overline" color="text.secondary">Roles</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1, mb: 2 }}>
                {roles.length ? roles.map((r) => <Chip key={r} label={r} color="primary" />) : <Typography color="text.disabled">none</Typography>}
              </Stack>
              <Divider sx={{ my: 2 }} />
              <Typography variant="overline" color="text.secondary">Canonical permissions</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                {canonical.length ? canonical.map((p) => (
                  <Chip key={p} label={p} variant="outlined" sx={{ fontFamily: 'monospace' }} />
                )) : <Typography color="text.disabled">none</Typography>}
              </Stack>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="overline" color="text.secondary">Product permissions</Typography>
              {Object.keys(product).length === 0 ? (
                <Typography color="text.disabled" sx={{ mt: 1 }}>none</Typography>
              ) : (
                <Box sx={{ mt: 1 }}>
                  {Object.entries(product).map(([module, perms]) => (
                    <Accordion key={module} disableGutters>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Chip label={module} size="small" color="secondary" sx={{ mr: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                          {perms.length} permission{perms.length !== 1 ? 's' : ''}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {perms.map((p) => <Chip key={p} label={p} size="small" sx={{ fontFamily: 'monospace' }} />)}
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>

          <JsonViewer title="Raw response" data={data} />
        </Stack>
      ) : (
        <Alert severity="info">Click <strong>Fetch</strong> to load entitlements.</Alert>
      )}
    </Box>
  );
};

export default Entitlements;
